// src/pages/dashboard/tabs/inventoryDash.tsx
import React, { useState, useEffect } from "react";
import { Row, Col, Statistic, Typography, Space, Empty } from "antd";
import { ShopOutlined, DollarOutlined, WarningOutlined, RiseOutlined, FallOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useQuery } from "@tanstack/react-query";
import { SectionCard, DataTable, StatusTag } from "../../../components/shared";
import { fetchProducts } from "../../../api/inventory/productApi";
import { fetchStockEntries } from "../../../api/inventory/stockApi";
import { fetchPurchaseOrders } from "../../../api/inventory/purchaseOrderApi";
import { fetchSalesOrders } from "../../../api/inventory/salesOrderApi";
import { useNotifications } from "../../../context/NotificationContext";
import { fetchClinicCompany } from "../../../api/admin/companyApi";
import { KPIItem } from "../components/DashboardKPIStrip";
import type { SalesOrder, PurchaseOrder } from "../../../types/inventory/OrderTypes";
import type { Product } from "../../../types/inventory/ProductTypes";
import type { Stock } from "../../../types/inventory/StockTypes";

const { Text } = Typography;

interface InventoryDashboardProps {
	startDate: Date;
	endDate: Date;
	refreshToken?: number;
	onKPIChange?: (kpis: KPIItem[]) => void;
}

const InventoryDashboard: React.FC<InventoryDashboardProps> = ({ refreshToken = 0 }) => {
	const [loading, setLoading] = useState<boolean>(true);
	const [products, setProducts] = useState<Product[]>([]);
	const [stockEntries, setStockEntries] = useState<Stock[]>([]);
	const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
	const [salesOrders, setSalesOrders] = useState<SalesOrder[]>([]);
	const { showError } = useNotifications();

	const { data: company } = useQuery({
		queryKey: ["clinic-company"],
		queryFn: fetchClinicCompany,
	});

	useEffect(() => {
		if (company) {
			loadDashboardData();
		}
	}, [company, refreshToken]);

	const loadDashboardData = async () => {
		setLoading(true);
		try {
			const [productsData, stockData, purchaseData, salesData] = await Promise.all([
				fetchProducts(1, 100, company.id),
				fetchStockEntries({ page: 1, per_page: 100 }),
				fetchPurchaseOrders({ page: 1, per_page: 10 }),
				fetchSalesOrders({ page: 1, per_page: 10 }),
			]);

			setProducts(productsData.data);
			setStockEntries(stockData.data);
			setPurchaseOrders(purchaseData.data);
			setSalesOrders(salesData.data);
		} catch (error) {
			console.error("Error loading dashboard data:", error);
			showError("Failed to load dashboard data");
		} finally {
			setLoading(false);
		}
	};

	// Calculate metrics
	const totalProducts = products.length;
	const totalStock = stockEntries.reduce((sum, entry) => sum + entry.quantity, 0);
	const availableStock = stockEntries.filter((entry) => entry.status === "available").reduce((sum, entry) => sum + entry.quantity, 0);
	const lowStock = stockEntries.filter((entry) => entry.status === "available" && entry.quantity < 10);

	const totalInventoryValue = products.reduce((sum, product) => {
		const stockQty = stockEntries.filter((s) => s.product?.product_id === product.product_id).reduce((total, s) => total + s.quantity, 0);
		return sum + (product.unit_cost || 0) * stockQty;
	}, 0);

	const recentPurchaseValue = purchaseOrders.slice(0, 5).reduce((sum, order) => {
		const amount = typeof order.total_amount === 'string' ? parseFloat(order.total_amount) : (order.total_amount || 0);
		return sum + amount;
	}, 0);

	const recentSalesValue = salesOrders.slice(0, 5).reduce((sum, order) => {
		const amount = typeof order.total_amount === 'string' ? parseFloat(order.total_amount) : (order.total_amount || 0);
		return sum + amount;
	}, 0);

	const lowStockColumns: ColumnsType<Stock> = [
		{
			title: "Product ID",
			dataIndex: ["product", "product_id"],
			key: "product_id",
		},
		{
			title: "Location",
			dataIndex: "location",
			key: "location",
			render: (text: string) => text || "—",
		},
		{
			title: "Quantity",
			dataIndex: "quantity",
			key: "quantity",
			render: (value: number) => (
				<Text type="danger" strong>
					{value}
				</Text>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status: string) => <StatusTag status={status} />,
		},
	];

	const recentOrdersColumns: ColumnsType<PurchaseOrder | SalesOrder> = [
		{
			title: "Order ID",
			key: "order_id",
			render: (_, record) => ("purchase_order_id" in record ? `PO-${record.purchase_order_id}` : `SO-${(record as SalesOrder).sales_order_id}`),
		},
		{
			title: "Date",
			dataIndex: "order_date",
			key: "order_date",
			render: (date: string) => (date ? new Date(date).toLocaleDateString() : "—"),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status: string) => <StatusTag status={status} />,
		},
		{
			title: "Amount",
			dataIndex: "total_amount",
			key: "total_amount",
			align: "right",
			render: (value: any, record: any, index: number) => {
				// Ensure we're working with individual values, not accumulated ones
				const amount = typeof value === 'string' ? parseFloat(value) : (typeof value === 'number' ? value : 0);
				return amount > 0 ? `$${amount.toFixed(2)}` : "—";
			},
		},
	];

	if (loading) {
		return <Empty description="Loading dashboard data..." style={{ marginTop: 80 }} />;
	}

	return (
		<>
			{/* Key Metrics */}
			<Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
				<Col xs={24} sm={12} lg={6}>
					<SectionCard>
						<Statistic title="Total Products" value={totalProducts} prefix={<ShopOutlined />} valueStyle={{ color: "var(--mg-primary)" }} />
					</SectionCard>
				</Col>
				<Col xs={24} sm={12} lg={6}>
					<SectionCard>
						<Statistic title="Total Stock Units" value={totalStock} suffix={`(${availableStock} available)`} valueStyle={{ color: "var(--mg-success)" }} />
					</SectionCard>
				</Col>
				<Col xs={24} sm={12} lg={6}>
					<SectionCard>
						<Statistic title="Inventory Value" value={totalInventoryValue} prefix={<DollarOutlined />} precision={2} valueStyle={{ color: "var(--mg-warning)" }} />
					</SectionCard>
				</Col>
			</Row>

			{/* Financial Overview */}
			<Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
				<Col xs={24} sm={12}>
					<SectionCard>
						<Statistic title="Recent Purchase Orders (Last 5)" value={recentPurchaseValue} prefix={<FallOutlined />} precision={2} valueStyle={{ color: "var(--mg-danger)" }} />
					</SectionCard>
				</Col>
				<Col xs={24} sm={12}>
					<SectionCard>
						<Statistic title="Recent Sales Orders (Last 5)" value={recentSalesValue} prefix={<RiseOutlined />} precision={2} valueStyle={{ color: "var(--mg-success)" }} />
					</SectionCard>
				</Col>
			</Row>

			{/* Low Stock Alert */}
			{lowStock.length > 0 && (
				<SectionCard
					title={
						<Space>
							<WarningOutlined style={{ color: "var(--mg-warning)" }} />
							<Text>Low Stock Alert ({lowStock.length} items)</Text>
						</Space>
					}
					style={{ marginBottom: 24 }}
				>
					<DataTable columns={lowStockColumns} dataSource={lowStock} rowKey="stock_id" pagination={false} />
				</SectionCard>
			)}

			{/* Recent Activity */}
			<Row gutter={[16, 16]}>
				<Col xs={24} lg={12}>
					<SectionCard title="Recent Purchase Orders">
						<DataTable
							columns={recentOrdersColumns}
							dataSource={purchaseOrders.slice(0, 5)}
							rowKey={(record) => 'purchase_order_id' in record ? (record as PurchaseOrder).purchase_order_id : (record as SalesOrder).sales_order_id}
							pagination={false}
						/>
					</SectionCard>
				</Col>
				<Col xs={24} lg={12}>
					<SectionCard title="Recent Sales Orders">
						<DataTable
							columns={recentOrdersColumns}
							dataSource={salesOrders.slice(0, 5)}
							rowKey={(record) => 'purchase_order_id' in record ? (record as PurchaseOrder).purchase_order_id : (record as SalesOrder).sales_order_id}
							pagination={false}
						/>
					</SectionCard>
				</Col>
			</Row>
		</>
	);
};

export default InventoryDashboard;
